/**
 * Carrying a hand-over request across a sign-in.
 *
 * The sign-in navigates away from `/authorize-app`, and the GitHub / GitLab
 * path leaves the app for the provider and comes back to `/github-auth`, so
 * nothing in the URL survives. The request is held in this tab's
 * `sessionStorage`, which survives both and which no other tab can see: an
 * abandoned hand-over in one tab never resumes in a sign-in made in another.
 *
 * It also rides the OAuth `state`, the way `nonHaRecipe` does
 * (`serializeAuthorizeAppRequest`), and is remembered again on the way back.
 *
 * Everything read back goes through `parseAuthorizeAppRequest`, so a value that
 * is corrupt, forged, or no longer servable reads as absent rather than
 * resuming a hand-over on terms nobody asked for.
 */
import { asQuery, AuthorizeAppRequest, parseAuthorizeAppRequest } from './z3-handover.util';

const CARRY_KEY = 'z3Handover';

/** Ten minutes: a provider round trip, and little enough that an abandoned tab expires. */
const CARRY_MAX_AGE_MS = 10 * 60 * 1000;

/** The wire form the OAuth `state` carries. */
export const serializeAuthorizeAppRequest = (request: AuthorizeAppRequest): string =>
  btoa(JSON.stringify(asQuery(request)));

export const rememberAuthorizeAppRequest = (request: AuthorizeAppRequest): void =>
  sessionStorage.setItem(CARRY_KEY, JSON.stringify({ at: Date.now(), request: asQuery(request) }));

export const forgetAuthorizeAppRequest = (): void => sessionStorage.removeItem(CARRY_KEY);

export const readCarriedAuthorizeAppRequest = (): AuthorizeAppRequest | null => {
  try {
    const { at, request } = JSON.parse(sessionStorage.getItem(CARRY_KEY) ?? 'null');
    return Date.now() - at < CARRY_MAX_AGE_MS ? parseAuthorizeAppRequest(request) : null;
  } catch (e) {
    return null;
  }
};

export const readHandoverFromAuthState = (
  encoded: string | undefined
): AuthorizeAppRequest | null => {
  if (!encoded) { return null; }
  try {
    return parseAuthorizeAppRequest(JSON.parse(atob(encoded)) as Record<string, string | undefined>);
  } catch (e) {
    return null;
  }
};

/**
 * Where a sign-in lands while a hand-over waits: the first active organization,
 * with no picker and no dashboard on the way — `onFullyAuthedResume$` goes back
 * to `/authorize-app` once it is stored. Null: land as usual.
 */
export const handoverLanding = (activeClientUsers: Array<{ id: string; }>): string | null =>
  readCarriedAuthorizeAppRequest() ? activeClientUsers[0]?.id ?? null : null;
